import React, { useEffect, useState } from 'react'
import { ActivityIndicator, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory, useLocation } from 'react-router-dom'

import { useStores, setPageTitle } from '@trivie/core'
import { Header, TabController, Button, color, iconArrowBack } from '@trivie/ui-mobile'
import { Text } from '@trivie/ui-mobile/components/text'
import { SubscriptionPathsTab } from './subscription-paths-tab'
import { SubscriptionTopicsTab } from './subscription-topics-tab'
import { SubscriptionQuizzesTab } from './subscription-quizzes-tab'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

const useQuery = () => {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

const formatExpirationDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const SubscriptionDetailScreen: React.FunctionComponent = observer(() => {
  const history = useHistory()
  const query = useQuery()
  const subscriptionId = Number(query.get('subscription_id')) || null
  const expiredFromList = query.get('expired') === 'true'
  const confirm = useConfirm()

  const { userStore, subscriptionStore } = useStores()
  const { user } = userStore
  const { selectedUserSubscription, userSubscriptionContentIds, getUserSubscriptionDetail } =
    subscriptionStore

  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    setPageTitle(`Subscription - ${process.env.REACT_APP_SITE_TITLE}`)
    if (user && subscriptionId) {
      setLoading(true)
      const load = async () => {
        try {
          const success = await getUserSubscriptionDetail(user.id, subscriptionId)
          if (!success) {
            history.goBack()
            confirm({
              title: `Error`,
              confirmationText: 'Okay',
              hideCancelButton: true,
              dialogText: `There was an issue while trying to load this subscription`,
            })
              .then(async () => {})
              .catch(() => {})
          }
        } catch (e) {
          console.error('Failed to load subscription detail:', e)
        } finally {
          setLoading(false)
        }
      }
      load()
    } else {
      setLoading(false)
    }
  }, [user?.id, subscriptionId])

  const purchase = selectedUserSubscription
  const topicIds = userSubscriptionContentIds.topic
  const pathIds = userSubscriptionContentIds.path
  const quizIds = userSubscriptionContentIds.quiz

  const isExpired = purchase?.isExpired || expiredFromList

  const tabs = [
    {
      id: 0,
      name: 'paths',
      label: 'Library.paths',
      component: <SubscriptionPathsTab contentUserIds={pathIds} />,
      onPress: () => setActiveTab(0),
      numeral: String(pathIds.length),
      enable: pathIds.length > 0,
    },
    {
      id: 1,
      name: 'topics',
      label: 'Library.topics',
      component: <SubscriptionTopicsTab contentUserIds={topicIds} />,
      onPress: () => setActiveTab(1),
      numeral: String(topicIds.length),
      enable: topicIds.length > 0,
    },
    {
      id: 2,
      name: 'quizzes',
      label: 'Library.quizzes',
      component: <SubscriptionQuizzesTab contentUserIds={quizIds} />,
      onPress: () => setActiveTab(2),
      numeral: String(quizIds.length),
      enable: quizIds.length > 0,
    },
  ]

  const hasContent = pathIds.length > 0 || topicIds.length > 0 || quizIds.length > 0

  // Auto-select first enabled tab
  useEffect(() => {
    if (!loading && hasContent) {
      const firstEnabled = tabs.find((t) => t.enable)
      if (firstEnabled) setActiveTab(firstEnabled.id)
    }
  }, [loading, pathIds.length, topicIds.length, quizIds.length])

  return (
    <div style={CONTAINER}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <div style={HEADER}>
            <Header leftIcon={iconArrowBack} onLeftPress={history.goBack} center />
          </div>
        </div>
      </div>
      {loading ? (
        <ActivityIndicator
          color="#999"
          style={{ flex: 1, height: window.innerHeight - 100 }}
          size="small"
        />
      ) : !loading && isExpired ? (
        <View style={EXPIRED_CONTAINER}>
          <Text
            variant="headline"
            text="🔒"
            style={{ textAlign: 'center', fontSize: 64, lineHeight: 72 }}
          />
          <Text
            variant="headline"
            text="Your subscription has expired"
            style={{ textAlign: 'center', fontSize: 20 }}
          />
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              marginTop: 24,
              marginBottom: 12,
            }}
          >
            <Text variant="body1" text="Your access to " style={{ color: color.shade70 }} />
            <Text
              variant="body1Emphasized"
              text={purchase?.product_title}
              style={{ color: color.shade70 }}
            />
            <Text variant="body1" text=" expired on " style={{ color: color.shade70 }} />
            <Text
              variant="body1Emphasized"
              text={purchase?.expiration_date ? formatExpirationDate(purchase.expiration_date) : ''}
              style={{ color: color.shade70 }}
            />
          </View>
          <Text
            variant="body1"
            text="Renew your subscription to continue learning"
            style={{ color: color.shade50, textAlign: 'center', marginTop: 8 }}
          />
          <Button
            variant="compact"
            textStyleOverrides={{ fontSize: 12, lineHeight: 12 }}
            text="Renew Subscription"
            onPress={
              userStore.externalStoreUrl
                ? () => {
                    window.open(
                      // @ts-ignore
                      userStore.externalStoreUrl,
                      '_blank',
                      'noopener',
                    )
                  }
                : undefined
            }
            style={{ marginTop: 32, padding: 4, paddingHorizontal: 8 }}
          />
        </View>
      ) : (
        <div style={CONTENT}>
          <View style={{ marginBottom: 8 }}>
            <Text style={{ marginBottom: 8 }} variant="headline" text={purchase?.product_title} />
            {purchase?.expiration_date ? (
              <Text
                text={`Your subscription expires on ${formatExpirationDate(purchase.expiration_date)}`}
                variant="body2"
                style={{ marginBottom: 16, color: color.shade50 }}
              />
            ) : null}
            {purchase?.description ? (
              <Text variant="body1" text={purchase.description} style={{ color: color.shade70 }} />
            ) : null}
          </View>
          {hasContent ? (
            <TabController
              matchID
              containerStyle={{
                marginTop: 20,
                marginBottom: 20,
                justifyContent: 'flex-start',
                paddingHorizontal: 0,
              }}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              tabs={tabs}
            />
          ) : (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <Text
                variant="body1"
                text="No content available for this subscription."
                style={{ color: color.shade50, textAlign: 'center' }}
              />
            </View>
          )}
        </div>
      )}
    </div>
  )
})

/**
 * Styling
 */
const CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
}
const CONTENT: React.CSSProperties = {
  flexDirection: 'column',
  justifyContent: 'center',
  display: 'flex',
  paddingBottom: 80,
  marginTop: 16,
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER: React.CSSProperties = { width: '100%' }
const EXPIRED_CONTAINER: ViewStyle = {
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: 48,
  height: '100%',
  top: -120,
}
